import { z } from 'zod';
/**
 * The shop (ADR 0031), over HTTP rather than the room: a purchase leaves the
 * game for Stripe and comes back by webhook, so it belongs to the account's
 * endpoints. The client names an offer; the server knows the price, opens the
 * Checkout, and delivers when Stripe says it was paid.
 */
/**
 * `POST /shop/checkout`: the character session says who buys, the body what.
 * `withdrawalWaiver` is the buyer's express request for immediate delivery
 * and acknowledgement of losing the right of withdrawal (code de la
 * consommation L221-28 13°, ADR 0044): no Checkout opens without it.
 */
export const ShopCheckoutRequestSchema = z
    .object({ offerId: z.string().min(1).max(64), withdrawalWaiver: z.literal(true) })
    .strict();
/**
 * The order opened, and how it gets paid. `embedded`: Stripe's payment page
 * mounts inside the game (Stripe.js, from its client secret and the
 * publishable key). `redirect`: the browser leaves for Stripe's hosted page,
 * when no publishable key is configured. `delivered`: a development server
 * without Stripe paid for pretend and the items are already in the bag.
 */
export const ShopCheckoutResponseSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('embedded'),
        orderId: z.uuid(),
        clientSecret: z.string().min(1),
        publishableKey: z.string().regex(/^pk_(test|live)_/),
    }),
    z.object({ kind: z.literal('redirect'), orderId: z.uuid(), url: z.url() }),
    z.object({ kind: z.literal('delivered'), orderId: z.uuid() }),
    // Paid for pretend in development, but the bag was full: it waits to be claimed.
    z.object({ kind: z.literal('paid'), orderId: z.uuid() }),
]);
/**
 * An order's life: opened (`pending`), paid but not yet in the bag (`paid`,
 * when the bag was full at delivery), `delivered`, or `cancelled` when the
 * Checkout expired unpaid. After payment (ADR 0044): `refunded` (refunded,
 * or a dispute lost), `disputed` (a dispute is open), `dispute_won` (the
 * dispute closed in our favour); the items are taken back from the first two.
 */
export const SHOP_ORDER_STATUSES = [
    'pending',
    'paid',
    'delivered',
    'cancelled',
    'refunded',
    'disputed',
    'dispute_won',
];
export const ShopOrderStatusSchema = z.enum(SHOP_ORDER_STATUSES);
export const ShopOrderSchema = z.object({
    id: z.uuid(),
    offerId: z.string().min(1),
    characterId: z.uuid(),
    status: ShopOrderStatusSchema,
    priceCents: z.number().int().min(0),
    currency: z.string().min(3).max(3),
    /** ISO 8601. */
    createdAt: z.string().min(1),
});
/** `GET /shop/orders`: the account's orders, newest first. */
export const ShopOrdersResponseSchema = z.object({ orders: z.array(ShopOrderSchema) });
/** `GET /shop/orders/:id` and `POST /shop/orders/:id/claim`: one order as it now stands. */
export const ShopOrderResponseSchema = z.object({ order: ShopOrderSchema });
export const SHOP_REFUSALS = [
    'unknown-offer',
    'unknown-order',
    /** No payment provider is configured: production without its keys. */
    'shop-unavailable',
    /** The order is not paid, or already delivered. */
    'not-claimable',
    'bag-full',
    /** The request does not carry the buyer's waiver of the right of withdrawal (ADR 0044). */
    'waiver-required',
    /** A guest account (no e-mail) cannot buy: its token lapses and the purchase with it. */
    'guest-account',
    /** A seasonal pack's offer outside its window (`SHOP_PACK_SEASONS`). */
    'off-season',
];
//# sourceMappingURL=shop.js.map